/*
 * Define a form page to display fields for a Subject.
 * First field is always a choice of Business or Individual.
 * The conditional fields are based on that choice.
 */
import * as yup from 'yup'
import React from 'react'
import css from './pages.module.css'
import { Divider } from '../components/divider'
import { relationshipToSubject, suffixes, businessCategories, individualCategories } from '../choices'
import { subjectType } from '../strings/types'
import {
  additionalIdLabel,
  addressLabel,
  address2Label,
  aliasLabel,
  alternatePhoneLabel,
  businessNameLabel,
  businessPhoneLabel,
  businessCategoryLabel,
  individualCategoryLabel,
  cellPhoneLabel,
  dbaLabel,
  dobLabel,
  einLabel,
  employerLabel,
  employerPhoneLabel,
  firstNameLabel,
  homeAddressLabel,
  homePhoneLabel,
  jobTitleLabel,
  lastNameLabel,
  middleNameLabel,
  npiLabel,
  optionalLabel,
  otherRelationshipLabel,
  personalEmailLabel,
  physicalDescLabel,
  suffixLabel,
  websiteLabel,
  workEmailLabel,
  workPhoneLabel
} from '../strings/labels'
import {
  addressLengthMessage,
  alternatePhoneMessage,
  businessNameMessage,
  businessNameLengthMessage,
  businessPhoneMessage,
  cellPhoneMessage,
  cityLengthMessage,
  einMessage,
  employerPhoneMessage,
  firstNameMessage,
  firstNameLengthMessage,
  genericMessage,
  homePhoneMessage,
  jobTitleLengthMessage,
  lastNameLengthMessage,
  lastNameMessage,
  makeSelectionMessage,
  maxLength150Message,
  maxLength500Message,
  middleNameLengthMessage,
  middleNameMessage,
  npiMessage,
  otherLengthMessage,
  personalEmailLengthMessage,
  personalEmailMessage,
  workEmailLengthMessage,
  workEmailMessage,
  workPhoneMessage,
  zipMessage,
  zipLengthMessage
} from '../strings/messages'
import {
  alphaRegex,
  einRegex,
  npiRegex,
  phoneRegex,
  zipRegex
} from '../strings/regex'
import { dateYupOptional, selectionRequired } from '../validators'

// Business headings
const SubjectDetailsHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Subject</h2>
)

const BusinessAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Business address</h2>
)

const SubjectContactHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Subject contact information</h2>
)

// Individual headings
const EmploymentInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Employment information</h2>
)

const ContactInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Subject contact information</h2>
)

const HomeAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">{homeAddressLabel}</h2>
)

const RelationshipHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Your relationship to subject</h2>
)

const ContentDivider = () => Divider

export const subjectPage = propOverrides => {
  const defaultDescription = `Please provide contact information that will help us identify the business or person responsible for the activity you are reporting. If you are unsure about a party's involvement, you may include them here. You will be able to make multiple entries.`

  const props = {
    index: 0,
    maxIndex: 9,
    pageId: 'subject_page',
    title: 'Identify the party responsible for the activity you are reporting',
    fieldType: 'radio',
    fieldName: 'subject_type',
    choices: [
      { label: subjectType.business, value: subjectType.business },
      { label: subjectType.individual, value: subjectType.individual }
    ],
    description: defaultDescription,
    label: 'Are you reporting about a business or an individual?',
    className: 'needed',
    labelClassName: 'needed',
    pageAfterSubject: 'report_on_behalf_page',
    ...propOverrides
  }

  if (props.index == 0) {
    props.choices.push({
      label: `I don't know who is responsible`,
      value: 'unknown'
    }) // Add the "I don't know who is responsible" option only on the first subject page
  }

  // Create unique field names based on the field name and the index value.
  const pageId = `${props.pageId}_${props.index}`
  const fieldName = `${props.fieldName}_${props.index}`
  const additional_identifying_information = `additional_identifying_information_${
    props.index
  }`
  const subj_business_name = `subj_business_name_${props.index}`
  const subj_business_category = `subj_business_category_${props.index}`
  const subj_business_dba = `subj_business_dba_${props.index}`
  const subj_alternate_phone = `subj_alternate_phone_${props.index}`
  const subj_business_phone = `subj_business_phone_${props.index}`
  const subj_business_address = `subj_business_address_${props.index}`
  const subj_business_employer_id = `subj_business_employer_id_${props.index}`
  const subj_business_npi = `subj_business_npi_${props.index}`
  const subj_first_name = `subj_first_name_${props.index}`
  const subj_middle_name = `subj_middle_name_${props.index}`
  const subj_last_name = `subj_last_name_${props.index}`
  const subj_suffix = `subj_suffix_${props.index}`
  const subj_alias_nickname = `subj_alias_nickname_${props.index}`
  const subj_individual_category = `subj_individual_category_${props.index}`
  const subj_home_phone = `subj_home_phone_${props.index}`
  const subj_cell_phone = `subj_cell_phone_${props.index}`
  const subj_work_phone = `subj_work_phone_${props.index}`
  const subj_employer = `subj_employer_${props.index}`
  const subj_employer_phone = `subj_employer_phone_${props.index}`
  const subj_employer_address = `subj_employer_address_${props.index}`
  const subj_personal_email = `subj_personal_email_${props.index}`
  const subj_work_email = `subj_work_email_${props.index}`
  const subj_address = `subj_address_${props.index}`
  const subj_dob = `subj_dob_${props.index}`
  const subj_job_title = `subj_job_title_${props.index}`
  const subj_physical_description = `subj_physical_description_${props.index}`
  const subj_relationship = `subj_relationship_${props.index}`
  const subj_relationship_other = `subj_relationship_other_${props.index}`
  const subj_website = `subj_website_${props.index}`

  // Define the validators for Business and Individual.
  // Initial selection of a subject_type is required.
  const subjectValidation = yup.object().shape({
    [fieldName]: selectionRequired(),
    [subj_business_name]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup
        .string()
        .required(businessNameMessage)
        .max(100, businessNameLengthMessage),
      otherwise: yup.string()
    }),
    [subj_business_category]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().required(makeSelectionMessage),
      otherwise: yup.string()
    }),
    [subj_business_dba]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().max(150, maxLength150Message),
      otherwise: yup.string()
    }),
    [subj_alternate_phone]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().matches(phoneRegex, {
        message: alternatePhoneMessage,
        excludeEmptyString: false
      }),
      otherwise: yup.string()
    }),
    [subj_business_phone]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().matches(phoneRegex, {
        message: businessPhoneMessage,
        excludeEmptyString: false
      }),
      otherwise: yup.string()
    }),
    [subj_business_address]: yup.object().when([fieldName], {
      is: subjectType.business,
      then: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      }),
      otherwise: yup.object()
    }),
    [subj_business_employer_id]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().matches(einRegex, {
        message: einMessage,
        excludeEmptyString: true
      }),
      otherwise: yup.string()
    }),
    [subj_business_npi]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().matches(npiRegex, {
        message: npiMessage,
        excludeEmptyString: true
      }),
      otherwise: yup.string()
    }),
    [additional_identifying_information]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().max(500, maxLength500Message),
      otherwise: yup.string()
    }),
    [subj_website]: yup.string().when([fieldName], {
      is: subjectType.business,
      then: yup.string().max(150, maxLength150Message),
      otherwise: yup.string()
    }),
    // Individual validations
    [subj_first_name]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup
        .string()
        .matches(alphaRegex, {
          message: firstNameMessage,
          excludeEmptyString: false
        })
        .max(20, firstNameLengthMessage)
        .required(firstNameMessage),
      otherwise: yup.string()
    }),
    [subj_individual_category]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().required(makeSelectionMessage),
      otherwise: yup.string()
    }),
    [subj_middle_name]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup
        .string()
        .matches(alphaRegex, {
          message: middleNameMessage,
          excludeEmptyString: false
        })
        .max(20, middleNameLengthMessage),
      otherwise: yup.string()
    }),
    [subj_last_name]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup
        .string()
        .matches(alphaRegex, {
          message: lastNameMessage,
          excludeEmptyString: false
        })
        .max(30, lastNameLengthMessage)
        .required(lastNameMessage),
      otherwise: yup.string()
    }),
    [subj_home_phone]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().matches(phoneRegex, {
        message: homePhoneMessage,
        excludeEmptyString: false
      }),
      otherwise: yup.string()
    }),
    [subj_cell_phone]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().matches(phoneRegex, {
        message: cellPhoneMessage,
        excludeEmptyString: false
      }),
      otherwise: yup.string()
    }),
    [subj_work_phone]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().matches(phoneRegex, {
        message: workPhoneMessage,
        excludeEmptyString: false
      }),
      otherwise: yup.string()
    }),
    [subj_employer_phone]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().matches(phoneRegex, {
        message: employerPhoneMessage,
        excludeEmptyString: false
      }),
      otherwise: yup.string()
    }),
    [subj_personal_email]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup
        .string()
        .email(personalEmailMessage)
        .max(250, personalEmailLengthMessage),
      otherwise: yup.string()
    }),
    [subj_work_email]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup
        .string()
        .email(workEmailMessage)
        .max(250, workEmailLengthMessage),
      otherwise: yup.string()
    }),
    [subj_address]: yup.object().when([fieldName], {
      is: subjectType.individual,
      then: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      }),
      otherwise: yup.object()
    }),
    [subj_employer]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().max(150, maxLength150Message),
      otherwise: yup.string()
    }),
    [subj_employer_address]: yup.object().when([fieldName], {
      is: subjectType.individual,
      then: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      }),
      otherwise: yup.object()
    }),
    [subj_job_title]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().max(30, jobTitleLengthMessage),
      otherwise: yup.string()
    }),
    [subj_dob]: yup.object().when([fieldName], {
      is: subjectType.individual,
      then: dateYupOptional(),
      otherwise: yup.object()
    }),
    [subj_relationship]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().required(makeSelectionMessage),
      otherwise: yup.string()
    }),
    [subj_relationship_other]: yup.string().when([subj_relationship], {
      is: 'Other',
      then: yup
        .string()
        .max(150, otherLengthMessage)
        .test('subj_rel_other_req', genericMessage, function(value) {
          const selectedValue = this.parent[[subj_relationship]]
          if (selectedValue && selectedValue.includes('Other'))
            return value != null
          return true
        }),
      otherwise: yup.string()
    }),
    [subj_alias_nickname]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().max(150, maxLength150Message),
      otherwise: yup.string()
    }),
    [subj_physical_description]: yup.string().when([fieldName], {
      is: subjectType.individual,
      then: yup.string().max(500, maxLength500Message),
      otherwise: yup.string()
    })
  })

  // Define the fields for Business
  let businessConditional = {
    value: subjectType.business,
    fields: [
      {
        type: 'content',
        content: SubjectDetailsHeading,
        name: 'subject_details'
      },
      {
        type: 'text',
        label: businessNameLabel,
        name: subj_business_name
      },
      {
        type: 'select',
        label: businessCategoryLabel,
        name: subj_business_category,
        choices:[
          {
            value: '',
            label: '',
          },
          ...businessCategories()
        ]
      },
      {
        type: 'text',
        label: dbaLabel,
        name: subj_business_dba,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: einLabel,
        name: subj_business_employer_id,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: npiLabel,
        name: subj_business_npi,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: additionalIdLabel,
        name: additional_identifying_information,
        requirementLabel: optionalLabel,
        multiline: true,
        rows: 4
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider1'
      },
      {
        type: 'content',
        content: BusinessAddressHeading,
        name: 'business_address'
      },
      {
        type: 'address',
        name: subj_business_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line1RequirementLabel: optionalLabel,
        line2Label: address2Label,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider2'
      },
      {
        type: 'content',
        content: SubjectContactHeading,
        name: 'subject_contact'
      },
      {
        type: 'text',
        label: businessPhoneLabel,
        name: subj_business_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: alternatePhoneLabel,
        name: subj_alternate_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: websiteLabel,
        name: subj_website,
        requirementLabel: optionalLabel
      }
    ]
  }

  // Define the fields for Individual
  let individualConditional = {
    value: subjectType.individual,
    fields: [
      {
        type: 'content',
        content: SubjectDetailsHeading,
        name: 'subject_details'
      },
      {
        type: 'text',
        label: firstNameLabel,
        name: subj_first_name
      },
      {
        type: 'text',
        label: middleNameLabel,
        name: subj_middle_name,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: lastNameLabel,
        name: subj_last_name
      },
      {
        type: 'select',
        label: suffixLabel,
        name: subj_suffix,
        choices: [
          {
            value: '',
            label: ''
          },
          ...suffixes()
        ],
        requirementLabel: optionalLabel
      },
      {
        type: 'select',
        label: individualCategoryLabel,
        name: subj_individual_category,
        choices:[
          {
            value: '',
            label: '',
          },
          ...individualCategories()
        ]
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider1'
      },
      {
        type: 'text',
        label: aliasLabel,
        name: subj_alias_nickname,
        requirementLabel: optionalLabel
      },
      {
        type: 'date',
        label: dobLabel,
        name: subj_dob,
        requirementLabel: optionalLabel,
        overrideDefaultMinMax: true
      },
      {
        type: 'text',
        label: physicalDescLabel,
        name: subj_physical_description,
        requirementLabel: optionalLabel,
        multiline: true,
        rows: 4
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider2'
      },
      {
        type: 'content',
        content: RelationshipHeading,
        name: 'relationship'
      },
      {
        type: 'radio',
        className: css.choices,
        label: '',
        name: subj_relationship,
        choices: relationshipToSubject(),
        conditionals: [
          {
            value: 'Other',
            fields: [
              {
                type: 'text',
                label: otherRelationshipLabel,
                name: subj_relationship_other,
                autoFocus: true
              }
            ]
          }
        ]
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider3'
      },
      {
        type: 'content',
        content: ContactInformationHeading,
        name: 'contact_information'
      },
      {
        type: 'text',
        label: homePhoneLabel,
        name: subj_home_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: cellPhoneLabel,
        name: subj_cell_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: workPhoneLabel,
        name: subj_work_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: personalEmailLabel,
        name: subj_personal_email,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: workEmailLabel,
        name: subj_work_email,
        requirementLabel: optionalLabel
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider4'
      },
      {
        type: 'content',
        content: HomeAddressHeading,
        name: 'mailing_address'
      },
      {
        type: 'address',
        name: subj_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider5'
      },
      {
        type: 'content',
        content: EmploymentInformationHeading,
        name: 'employment_information'
      },
      {
        type: 'text',
        label: jobTitleLabel,
        name: subj_job_title,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: employerLabel,
        name: subj_employer,
        requirementLabel: optionalLabel
      },
      {
        type: 'address',
        name: subj_employer_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'text',
        label: employerPhoneLabel,
        name: subj_employer_phone,
        requirementLabel: optionalLabel
      }
    ]
  }

  let addAnotherConditional = []
  let addAnotherAction = {}
  // When not on the last page, give option to add another subject
  if (props.index < props.maxIndex) {
    const { addAction, addCondition } = addAnother(
      props,
      subjectType.business,
      subjectType.individual
    )
    addAnotherAction = addAction
    addAnotherConditional = [addCondition]
  }

  return {
    id: pageId,
    title: props.title,
    description: props.description,
    validation: subjectValidation,
    fields: [
      {
        type: props.fieldType,
        name: fieldName,
        className: props.className,
        choices: props.choices,
        label: props.label,
        labelClassName: props.labelClassName,
        conditionals: [businessConditional, individualConditional].concat(
          addAnotherConditional
        )
      }
    ],
    ...addAnotherAction
  }
}

// Define the field and action to allow the user to add another subject
const addAnother = (props, business, individual) => {
  const add_subject = `add_subject_${props.index}`
  const nextSubjectPage = `${props.pageId}_${props.index + 1}`
  const addCondition = {
    value: [business, individual],
    fields: [
      {
        type: 'checkbox',
        label: '',
        name: add_subject,
        choices: [
          {
            label: `Add another subject (up to ${props.maxIndex + 1} subjects)`,
            value: 'True'
          }
        ],
        requirementLabel: optionalLabel
      }
    ]
  }
  const addAction = {
    action: {
      type: 'goto',
      pageId: values => {
        const add = values[add_subject]
        return add.length > 0 ? nextSubjectPage : props.pageAfterSubject
      }
    }
  }
  return {
    addAction,
    addCondition
  }
}
